<!--
  UsageTable.vue
  Callbot monthly settlement - hospital usage table (screen spec 1 "columns", "groups", "the whole row is a link"),
  laid out like the approved 2026-10-27 Main mockup.

  Request: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 1", "요청 계약 1" (`items[]`)
  - Columns: 병원 (+ deleted-company badge) · 상담사 연결 방식 (pill; vox direct n / requests m in its title) ·
    통화 (web tests excluded; sub "발신 n · AI 전 끊김 n") · 웹 테스트 · AI 응대 시간 (`미종료 n` badge on its own sub line) ·
    상담사 연결 응답 / 요청 · 상담사 통화 시간 · trailing `›`. Zero renders dim, non-numeric values (`"…"`) as '—'.
    Counts carry no unit (the header names it); durations show hours + minutes, exact value in `title`.
  - Sorting is owned by the parent (query `sort`). Clicking a header emits('sort', key) - the parent picks the next
    direction with settlementRules.toggleUsageSort. The sorted column carries `aria-sort` and an arrow.
  - grouped (chip `전체`): hospital rows first, internal/test rows below a "내부·테스트" header row. The order received
    (already sorted by the parent) is kept inside each group - sorting happens within groups.
  - Row -> hospital detail (/callbot/settlement/:companyId?month=<same month>). Deleted companies (`company_deleted`)
    get a "삭제된 회사" badge next to the name (reply table 6); their detail opens like any other (reply "2026-10-19").
  - The web test column is read from vox, so before the first auto close (isWebCountPending) it is usually 0 - the
    header carries "마감 뒤 확정" with a tooltip (reply table 7).

  Row link structure (semantic table, yet the whole row is a real link):
  - Each row has one router-link (custom) giving href/navigate. The hospital name in the first cell (<th scope="row">)
    is the row's only tab stop (Enter opens it); its ::after covers the whole first cell.
  - Every other cell holds an empty cover link (same href, tabindex -1, aria-hidden) spanning the cell, so a click
    anywhere keeps native link behaviour (ctrl/cmd, middle click, "open in new tab") while screen readers read the
    cell values and hear one link per row. Using <tr> as the positioning box is uneven across browsers, hence per cell.
  - navigate leaves modifier / middle clicks to the browser (vue-router guardEvent).

  Export selection (plan ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-1, contract stage 2·3 4-1):
  - `selectable` adds a leading checkbox cell (`<td class="usage-row__select">`). That cell has no cover link, so
    ticking a box never opens the row, and the box is not nested in the name link. Tab order: box -> name link.
    The row's :has(:focus-visible) highlight only reacts to the link, so the box has its own focus ring.
  - The header box selects / clears the visible rows only (`items` as given - the parent applies 'toggle-all' to
    them), indeterminate when some are picked. It is not a sort button, so it carries no aria-sort and no text (the
    header text stays as it was).
  - The selection itself (Set of company_id strings) belongs to the parent (SettlementList) - props/emits only.
  - Settlement start: a gray tag next to the name ('정산 대상 아님' / '시작 10/10') from settlementScopeView(item).tag
    with its tooltip; none when the start date is unset or the month is after the start month.
-->
<template>
  <div class="usage-table">
    <table class="usage-table__table" :class="{ 'is-selectable': selectable }">
      <caption class="usage-table__caption">{{ caption }}</caption>
      <colgroup>
        <col v-if="selectable" class="usage-table__col-select" />
        <col class="usage-table__col-name" />
        <col class="usage-table__col-connect" />
        <col class="usage-table__col-calls" />
        <col class="usage-table__col-web" />
        <col class="usage-table__col-ai" />
        <col class="usage-table__col-handoff" />
        <col class="usage-table__col-talk" />
        <col class="usage-table__col-chev" />
      </colgroup>

      <thead>
        <tr>
          <th v-if="selectable" scope="col" class="usage-table__th usage-table__th--select">
            <a-checkbox
              class="usage-table__select-box"
              :checked="headerState.checked"
              :indeterminate="headerState.indeterminate"
              :disabled="!visibleIds.length"
              :aria-label="SELECT_ALL_LABEL"
              @change="onToggleAll"
            />
          </th>
          <th
            v-for="col in columns"
            :key="col.key"
            scope="col"
            class="usage-table__th"
            :class="{ 'is-num': col.numeric, 'is-sorted': currentSort.key === col.key }"
            :aria-sort="ariaSortOf(col.key)"
          >
            <button
              type="button"
              class="usage-table__sort"
              :title="col.tip || SORT_TITLE"
              @click="emit('sort', col.key)"
            >
              <span class="usage-table__sort-label">
                {{ col.label }}
                <span v-if="col.hint" class="usage-table__sort-hint">{{ col.hint }}</span>
              </span>
              <span class="usage-table__arrow" aria-hidden="true">
                <ArrowUpOutlined v-if="isSortedBy(col.key, 'asc')" />
                <ArrowDownOutlined v-else-if="isSortedBy(col.key, 'desc')" />
              </span>
            </button>
          </th>
          <td class="usage-table__th usage-table__th--chev" aria-hidden="true"></td>
        </tr>
      </thead>

      <!-- Nothing left after filtering - the parent supplies the text and the reset action -->
      <tbody v-if="!rows.length">
        <tr>
          <td :colspan="COLSPAN" class="usage-table__empty">
            <slot name="empty" />
          </td>
        </tr>
      </tbody>

      <template v-else>
        <tbody v-for="group in groups" :key="group.key" class="usage-table__group">
          <tr v-if="group.header" class="usage-table__group-head">
            <th scope="rowgroup" :colspan="COLSPAN">
              {{ group.header }}
              <span class="usage-table__group-count">{{ group.rows.length }}곳</span>
            </th>
          </tr>

          <router-link v-for="row in group.rows" :key="row.id" v-slot="{ href, navigate }" :to="row.to" custom>
            <tr class="usage-row" :class="{ 'is-internal': row.internal, 'is-selected': selectable && isSelected(row.id) }">
              <!-- No cover link here: ticking the box never opens the row -->
              <td v-if="selectable" class="usage-row__select">
                <a-checkbox
                  class="usage-row__select-box"
                  :checked="isSelected(row.id)"
                  :aria-label="`${row.name || row.id} 선택`"
                  @change="(e) => onToggle(row.id, e)"
                />
              </td>
              <th scope="row" class="usage-row__head">
                <a
                  :href="href"
                  class="usage-row__link"
                  :aria-describedby="row.tag ? scopeTipId(row.id) : undefined"
                  @click="navigate"
                >
                  <span class="usage-row__name" :class="{ 'is-missing': !row.name }">{{ row.name || row.id }}</span>
                  <span v-if="row.deleted" class="usage-row__deleted" :title="DELETED_TITLE">
                    {{ DELETED_COMPANY_LABEL }}
                  </span>
                  <a-tooltip v-if="row.tag" :title="row.tag.tooltip">
                    <span class="usage-row__scope" :class="`is-${row.scopeKind}`">{{ row.tag.text }}</span>
                  </a-tooltip>
                </a>
                <!-- The tag tooltip for screen readers and keyboard users (the tooltip itself is hover only) -->
                <span v-if="row.tag" :id="scopeTipId(row.id)" class="usage-row__sr">{{ row.tag.tooltip }}</span>
              </th>

              <td class="usage-row__cell">
                <span
                  v-if="row.connect.pill"
                  class="usage-row__mode"
                  :class="`is-${row.connect.mode}`"
                  :title="row.connect.sub || undefined"
                >
                  {{ row.connect.label }}
                </span>
                <span v-else class="usage-row__plain is-zero">{{ row.connect.label }}</span>
                <a
                  :href="href"
                  class="usage-row__cover"
                  tabindex="-1"
                  aria-hidden="true"
                  :title="row.connect.sub || undefined"
                  @click="navigate"
                ></a>
              </td>

              <td class="usage-row__cell is-num">
                <span class="usage-row__num" :class="{ 'is-zero': row.calls.muted }">{{ row.calls.text }}</span>
                <span v-if="row.callsSub" class="usage-row__sub">{{ row.callsSub }}</span>
                <a :href="href" class="usage-row__cover" tabindex="-1" aria-hidden="true" @click="navigate"></a>
              </td>

              <td class="usage-row__cell is-num">
                <span class="usage-row__num" :class="{ 'is-zero': row.web.muted }">{{ row.web.text }}</span>
                <a
                  :href="href"
                  class="usage-row__cover"
                  tabindex="-1"
                  aria-hidden="true"
                  :title="webPending ? WEB_PENDING_TOOLTIP : undefined"
                  @click="navigate"
                ></a>
              </td>

              <td class="usage-row__cell is-num">
                <span class="usage-row__num" :class="{ 'is-zero': row.ai.muted }">{{ row.ai.text }}</span>
                <!-- Own line so the duration stays flush right with the other rows -->
                <span v-if="row.notFinal" class="usage-row__sub">
                  <span class="usage-row__chip">미종료 {{ row.notFinal }}</span>
                </span>
                <a
                  :href="href"
                  class="usage-row__cover"
                  tabindex="-1"
                  aria-hidden="true"
                  :title="row.ai.title"
                  @click="navigate"
                ></a>
              </td>

              <td class="usage-row__cell is-num">
                <span class="usage-row__num" :class="{ 'is-zero': row.handoff.muted }">{{ row.handoff.text }}</span>
                <a :href="href" class="usage-row__cover" tabindex="-1" aria-hidden="true" @click="navigate"></a>
              </td>

              <td class="usage-row__cell is-num">
                <span class="usage-row__num" :class="{ 'is-zero': row.talk.muted }">{{ row.talk.text }}</span>
                <a
                  :href="href"
                  class="usage-row__cover"
                  tabindex="-1"
                  aria-hidden="true"
                  :title="row.talk.title"
                  @click="navigate"
                ></a>
              </td>

              <td class="usage-row__cell usage-row__chev">
                <RightOutlined aria-hidden="true" />
                <a :href="href" class="usage-row__cover" tabindex="-1" aria-hidden="true" @click="navigate"></a>
              </td>
            </tr>
          </router-link>
        </tbody>
      </template>
    </table>
  </div>
</template>

<script setup>
import { ArrowDownOutlined, ArrowUpOutlined, RightOutlined } from '@ant-design/icons-vue';
import { computed } from 'vue';

import {
  EMPTY,
  formatDuration,
  formatNumber,
  isNumber,
  isValidMonth,
  monthLabel,
} from '../service/settlementFormat';
import { headerCheckState } from '../service/settlementExportSelection';
import { settlementScopeView } from '../service/settlementStart';
import {
  CATEGORY_LABELS,
  DELETED_COMPANY_LABEL,
  WEB_PENDING_HINT,
  WEB_PENDING_TOOLTIP,
  callsSubLabel,
  connectModeCell,
  groupUsageItems,
  isWebCountPending,
  normalizeUsageSort,
  settlementDetailLocation,
} from '../service/settlementRules';

const props = defineProps({
  /** Rows to show (contract 1 `items[]`) - already filtered by chip/search and sorted by the parent */
  items: { type: Array, required: true },
  /** 'YYYY-MM' - the `month` of each row link */
  month: { type: String, required: true },
  /** Current sort { key, dir } - header arrow and aria-sort */
  sort: { type: Object, required: true },
  /** Chip `전체` - hospitals first, internal/test below their own header row */
  grouped: { type: Boolean, default: true },
  /** Leading select column for the export (the parent passes canExport) */
  selectable: { type: Boolean, default: false },
  /** Selected company ids - Set<string> owned by the parent (null = nothing selected) */
  selectedIds: { type: Object, default: null },
});

/**
 * sort(key) · toggle(companyId: string, checked: boolean) · toggle-all(checked: boolean) - the parent applies
 * toggle-all to the rows it passed in (`items`, the visible rows)
 */
const emit = defineEmits(['sort', 'toggle', 'toggle-all']);

/** Headers - key is the query `sort` key (settlementRules.USAGE_SORT_KEYS) */
const COLUMNS = [
  { key: 'name', label: '병원', numeric: false },
  { key: 'connect', label: '상담사 연결 방식', numeric: false },
  { key: 'calls', label: '통화', numeric: true },
  { key: 'web', label: '웹 테스트', numeric: true },
  { key: 'ai', label: 'AI 응대 시간', numeric: true },
  { key: 'handoff', label: '상담사 연결 응답 / 요청', numeric: true },
  { key: 'talk', label: '상담사 통화 시간', numeric: true },
];
/** Data columns + the trailing chevron column (+ the leading select column) */
const COLSPAN = computed(() => COLUMNS.length + 1 + (props.selectable ? 1 : 0));

const SELECT_ALL_LABEL = '보이는 병원 모두 선택';

const SORT_TITLE = '눌러서 정렬합니다';
const DELETED_TITLE = '삭제된 회사입니다. 그 달 사용량이 있어 정산·자동 마감 대상입니다.';
const NOT_FINAL_TITLE = '아직 끝나지 않은 통화가 있어 사용량이 덜 찼습니다. 정산에 주는 영향은 병원 월 정산에서 봅니다.';

/** Months whose web test count is not read from vox yet (before close) - header hint "마감 뒤 확정" */
const webPending = computed(() => isWebCountPending(props.month));

const columns = computed(() =>
  COLUMNS.map((col) =>
    col.key === 'web' && webPending.value ? { ...col, hint: WEB_PENDING_HINT, tip: WEB_PENDING_TOOLTIP } : col
  )
);

const currentSort = computed(() => normalizeUsageSort(props.sort));

function isSortedBy(key, dir) {
  return currentSort.value.key === key && currentSort.value.dir === dir;
}

/** Only the sorted column carries the attribute */
function ariaSortOf(key) {
  if (currentSort.value.key !== key) return undefined;
  return currentSort.value.dir === 'asc' ? 'ascending' : 'descending';
}

const caption = computed(() => {
  const col = columns.value.find((c) => c.key === currentSort.value.key);
  const sortText = col ? `${col.label} ${currentSort.value.dir === 'asc' ? '오름차순' : '내림차순'} 정렬` : '';
  const monthText = isValidMonth(props.month) ? `${monthLabel(props.month)} ` : '';
  return `${monthText}병원 사용량${sortText ? ` — ${sortText}` : ''}. 통화는 웹 테스트를 뺀 전화입니다. 병원을 누르면 그 병원의 월 정산으로 이동합니다.`;
});

/** Hours + minutes from one hour up, minutes + seconds below (exact value goes to the title) */
function compactDuration(sec) {
  if (!isNumber(sec)) return EMPTY;
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  if (h === 0) return formatDuration(total);
  return `${formatNumber(h)}시간 ${Math.floor((total % 3600) / 60)}분`;
}

/** Number cell - zero and non-numbers ('—') render dim */
function numCell(raw, text) {
  return { text, muted: !isNumber(raw) || raw === 0 };
}

/** Duration cell - compact text, exact value in the title */
function durationCell(sec, extraTitle = '') {
  const exact = isNumber(sec) ? formatDuration(sec) : '';
  const title = [exact, extraTitle].filter(Boolean).join(' · ');
  return { ...numCell(sec, compactDuration(sec)), title: title || undefined };
}

/** answered / requested - a single '—' when neither is a number */
function handoffCell(item) {
  const { answered, handoffs } = item;
  if (!isNumber(answered) && !isNumber(handoffs)) return { text: EMPTY, muted: true };
  return {
    text: `${formatNumber(answered)} / ${formatNumber(handoffs)}`,
    muted: !(isNumber(answered) && answered > 0) && !(isNumber(handoffs) && handoffs > 0),
  };
}

/** Modes drawn as a pill - `없음` and unknown values are dim text */
const CONNECT_PILL_MODES = ['pbx', 'vox_direct', 'mixed'];

function toRow(item) {
  const name = typeof item.company_name === 'string' ? item.company_name.trim() : '';
  const internal = item.internal === true;
  const deleted = item.company_deleted === true;
  const mode = item.connect_mode?.mode ?? null;
  const connect = connectModeCell(item);
  const notFinal = isNumber(item.not_final) && item.not_final > 0 ? formatNumber(item.not_final) : '';
  const scope = settlementScopeView(item);
  return {
    id: String(item.company_id),
    name,
    internal,
    deleted,
    to: settlementDetailLocation(item.company_id, props.month, undefined, { companyName: name }),
    connect: { mode, pill: CONNECT_PILL_MODES.includes(mode), label: connect.label, sub: connect.sub },
    calls: numCell(item.calls, formatNumber(item.calls)),
    callsSub: callsSubLabel(item),
    web: numCell(item.web, formatNumber(item.web)),
    ai: durationCell(item.ai_seconds, notFinal ? NOT_FINAL_TITLE : ''),
    notFinal,
    handoff: handoffCell(item),
    talk: durationCell(item.counselor_talk_seconds),
    // '정산 대상 아님' / '시작 10/10' - null when unset or after the start month
    tag: scope?.tag ?? null,
    scopeKind: scope?.kind ?? 'unset',
  };
}

const rows = computed(() => props.items.filter((it) => it && it.company_id).map(toRow));

// ---- export selection ----
const visibleIds = computed(() => rows.value.map((row) => row.id));

function isSelected(id) {
  const set = props.selectedIds;
  return !!set && typeof set.has === 'function' && set.has(id);
}

const headerState = computed(() => headerCheckState(props.selectedIds, visibleIds.value));

function onToggle(id, event) {
  emit('toggle', id, !!event?.target?.checked);
}

/** Indeterminate counts as "not all" - one click selects every visible row */
function onToggleAll(event) {
  emit('toggle-all', !!event?.target?.checked);
}

/** id of the screen-reader copy of a row's tag tooltip (company ids are plain ids, made safe for an id anyway) */
function scopeTipId(id) {
  return `usage-scope-tip-${String(id).replace(/[^A-Za-z0-9_-]/g, '_')}`;
}

const groups = computed(() => {
  if (!props.grouped) return [{ key: 'all', header: '', rows: rows.value }];
  const { hospitals, internals } = groupUsageItems(rows.value);
  const out = [];
  if (hospitals.length) out.push({ key: 'hospital', header: '', rows: hospitals });
  if (internals.length) out.push({ key: 'internal', header: CATEGORY_LABELS.internal, rows: internals });
  return out;
});
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

$row-hover-bg: #f7f9fc;
$head-bg: #fafbfc;
$row-border: #eef0f3;
$select-col-width: 44px;

// On narrow widths only the table scrolls sideways, never the page
.usage-table {
  overflow-x: auto;
}

// Fits a 1280px laptop with the sidebar open (content about 960px) without horizontal scroll
.usage-table__table {
  width: 100%;
  min-width: 960px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  color: $st-text;

  // The select column adds its own width only when it is shown
  &.is-selectable {
    min-width: 960px + $select-col-width;
  }
}

.usage-table__caption {
  @include st-sr-only;
}

// Numeric columns get their content width; the hospital column takes the rest
.usage-table__col-select {
  width: $select-col-width;
}
.usage-table__col-connect {
  width: 150px;
}
.usage-table__col-calls {
  width: 160px;
}
.usage-table__col-web {
  width: 96px;
}
.usage-table__col-ai {
  width: 160px;
}
.usage-table__col-handoff {
  width: 170px;
}
.usage-table__col-talk {
  width: 130px;
}
.usage-table__col-chev {
  width: 32px;
}

// ---- headers ----
.usage-table__th {
  padding: 10px 14px;
  border-bottom: 1px solid $st-border-strong;
  background: $head-bg;
  color: $st-text-secondary;
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
  vertical-align: bottom;
  white-space: nowrap;

  &.is-num {
    text-align: right;
  }

  &.is-sorted {
    color: $st-text;
  }
}

// Undo the global `button { height: 40px }` (assets/scss/common.scss) so the header row fits its text
.usage-table__sort {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: inherit;
  cursor: pointer;

  &:hover {
    color: $st-text;
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
    border-radius: 4px;
  }
}

.usage-table__sort-label {
  display: inline-flex;
  flex-direction: column;
  line-height: 1.35;

  .is-num & {
    align-items: flex-end;
  }
}

.usage-table__sort-hint {
  font-size: 11px;
  font-weight: 500;
  color: $st-muted;
}

// Numeric columns: arrow before the label, so the label text ends at the column edge like the numbers below
.usage-table__th.is-num .usage-table__sort {
  flex-direction: row-reverse;
}

// Fixed width so headers do not shift when the sorted column changes
.usage-table__arrow {
  display: inline-flex;
  width: 10px;
  font-size: 10px;
  color: $st-accent;
}

// Header select box - no text, no sort button
.usage-table__th--select {
  padding-right: 0;
  vertical-align: middle;
}

// ---- empty result, group header ----
.usage-table__empty {
  padding: 32px 16px;
  text-align: center;
}

.usage-table__group-head th {
  padding: 8px 14px;
  border-bottom: 1px solid $row-border;
  background: $head-bg;
  color: $st-text-secondary;
  font-size: 12.5px;
  font-weight: 600;
  text-align: left;
}

.usage-table__group + .usage-table__group .usage-table__group-head th {
  border-top: 1px solid $st-border-strong;
}

.usage-table__group-count {
  margin-left: 6px;
  font-weight: 400;
  color: $st-muted;
}

// ---- rows ----
.usage-row {
  > th,
  > td {
    position: relative;
    padding: 13px 14px;
    // !important — CertiAccManage.vue 의 전역 td { border-bottom: transparent !important } 가 td 줄을 지워 항목 칸(th)에만 줄이 남는다
    border-bottom: 1px solid $row-border !important;
    background: #fff;
    text-align: left;
    vertical-align: middle;
    font-weight: 400;
  }

  > .is-num {
    text-align: right;
  }

  &.is-internal > th,
  &.is-internal > td {
    background: $st-bg;
  }

  &:hover > th,
  &:hover > td {
    background: $row-hover-bg;
  }
}

.usage-table__group:last-child .usage-row:last-child > * {
  border-bottom: 0 !important;
}

// First cell - the hospital name is the row link; ::after covers the cell
.usage-row__link {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  color: inherit;
  text-decoration: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }

  &:focus-visible {
    outline: none;
  }
}

// Keyboard focus highlights the whole row (browsers without :has get an outline on the link itself)
.usage-row:has(.usage-row__link:focus-visible) {
  > th,
  > td {
    background: $st-accent-bg;
  }

  > th {
    box-shadow: inset 3px 0 0 $st-accent;
  }
}

@supports not selector(:has(*)) {
  .usage-row__link:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
  }
}

.usage-row__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: $st-text;
  word-break: keep-all;

  &.is-missing {
    font-family: monospace;
    font-weight: 400;
    color: $st-muted;
  }

  .is-internal & {
    color: $st-text-secondary;
  }
}

// Deleted company - gray (still settled and auto closed, detail opens)
.usage-row__deleted {
  @include st-pill($st-text-secondary, #f1f2f4, #e4e6ea);
  font-weight: 500;
  cursor: help;
}

// Every other cell - an empty cover link with the same href
.usage-row__cover {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.usage-row__cell {
  white-space: nowrap;
}

// Value on one line, sub line below
.usage-row__num,
.usage-row__plain,
.usage-row__sub {
  display: block;
}

.usage-row__num {
  @include st-num;
  color: $st-text;

  .is-internal & {
    color: $st-text-secondary;
  }

  &.is-zero {
    color: $st-muted;
  }
}

.usage-row__plain {
  font-size: 13px;

  &.is-zero {
    color: $st-muted;
  }
}

.usage-row__sub {
  @include st-num;
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.35;
  color: $st-muted;
}

// Counselor connect mode - the route actually used that month
.usage-row__mode {
  @include st-pill(#3b4450, #eef1f5, #eef1f5);
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
}

// 미종료 n - usage is not complete yet (not a blocking state); sits on the sub line under the duration
.usage-row__chip {
  @include st-pill($st-warning, $st-warning-bg, $st-warning-bg);
  font-size: 11.5px;
}

// ---- export selection ----
// Keyboard focus on the box gets its own ring (the row highlight only follows the name link)
.usage-table__select-box,
.usage-row__select-box {
  :deep(.ant-checkbox-input:focus-visible + .ant-checkbox-inner) {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
  }
}

// No cover link in this cell - a click beside the box does nothing rather than opening the row
.usage-row > .usage-row__select {
  padding-right: 0;
  cursor: default;
}

.usage-row.is-selected > th,
.usage-row.is-selected > td {
  background: $st-accent-bg;
}

// Settlement start tag next to the name - gray like the deleted badge. Positioned above the link's ::after cover so
// hovering it opens its tooltip (a click still lands in the link and opens the row)
.usage-row__scope {
  @include st-pill($st-text-secondary, #f1f2f4, #e4e6ea);
  position: relative;
  z-index: 1;
  font-weight: 500;
  cursor: help;

  // Start month is still billed (from the start date) - outlined, lighter than the not-billable tag
  &.is-start_month {
    @include st-pill-tone($st-text-secondary, #fff, $st-border-strong);
  }
}

.usage-row__sr {
  @include st-sr-only;
}

.usage-row > .usage-row__chev {
  padding-left: 0;
  padding-right: 14px;
  color: #9aa1aa;
  font-size: 12px;
  text-align: right;
}

.usage-row:hover > .usage-row__chev {
  color: $st-accent;
}
</style>
